import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CHART_CHROME } from "@/shared/ui/chart/public";
import { HELD_COLOR, MARK, USED_COLOR } from "./chartTheme";
import type { UsageTrendChartProps } from "./contract";

/**
 * 버킷별 보유(선)와 사용(면)을 한 축에 그리는 추세 차트다. 두 선 사이의 빈 면이 유휴다.
 *
 * 다른 추세 차트와 다른 점은 두 가지다.
 *
 * 1. **y 축을 0 에서 시작한다.** 보는 것이 크기(얼마나 썼는가)와 두 값의 차이다. 0 을 자르면 면의
 *    넓이가 크기를 과장한다.
 * 2. **결측을 연결하지 않는다**(`connectNulls={false}`). 표본이 없는 버킷은 값이 0 이 아니라
 *    없는 구간이다. 선이 끊긴 자리가 표본 공백이다.
 */
export function UsageTrendChart({
  points,
  heldLabel,
  usedLabel,
  formatValue,
  ariaLabel,
}: UsageTrendChartProps) {
  return (
    <div role="img" aria-label={ariaLabel}>
      <ResponsiveContainer width="100%" height={MARK.height}>
        {/* 위 여백은 최댓값 눈금 글자가 카드 경계에 닿지 않을 만큼 둔다. */}
        <ComposedChart data={[...points]} margin={{ top: 14, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={CHART_CHROME.grid} vertical={false} />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={false}
            stroke={CHART_CHROME.axis}
            fontSize={MARK.tickFontSize}
            minTickGap={24}
          />
          <YAxis
            domain={[0, "auto"]}
            width={56}
            tickFormatter={(tick: number) => formatValue(tick)}
            tickLine={false}
            axisLine={false}
            stroke={CHART_CHROME.axis}
            fontSize={MARK.tickFontSize}
          />
          <Tooltip
            formatter={(value: number, name: string) => [formatValue(value), name]}
            contentStyle={{
              background: CHART_CHROME.tooltipBg,
              border: `1px solid ${CHART_CHROME.tooltipBorder}`,
              color: CHART_CHROME.tooltipInk,
              fontSize: MARK.tickFontSize,
            }}
            itemStyle={{ color: CHART_CHROME.tooltipInk }}
          />
          {/* 범례 글자는 계열 색이 아니라 본문 글자색을 쓴다. 색은 옆의 선 표식이 말한다. */}
          <Legend
            iconType="plainline"
            wrapperStyle={{ fontSize: MARK.legendFontSize }}
            formatter={(value: string) => (
              <span style={{ color: CHART_CHROME.tooltipInk }}>{value}</span>
            )}
          />
          <Area
            name={usedLabel}
            dataKey="used"
            type="linear"
            stroke={USED_COLOR}
            strokeWidth={MARK.strokeWidth}
            fill={USED_COLOR}
            fillOpacity={MARK.areaOpacity}
            dot={false}
            connectNulls={false}
            isAnimationActive={false}
          />
          <Line
            name={heldLabel}
            dataKey="held"
            type="linear"
            stroke={HELD_COLOR}
            strokeWidth={MARK.strokeWidth}
            dot={false}
            connectNulls={false}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
